import React, { useState } from 'react';
import {
  Button,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  TextField
} from '@mui/material';
import { FlaskConical } from 'lucide-react';

interface TestRunButtonProps {
  onTestRun: (keywordLimit: number) => void;
  disabled?: boolean;
  quickLimits?: number[];
  currentLimit?: number | null;
}

const defaultQuickLimits = [50, 100, 200, 500];

const TestRunButton: React.FC<TestRunButtonProps> = ({
  onTestRun,
  disabled = false,
  quickLimits = defaultQuickLimits,
  currentLimit = null
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [customLimit, setCustomLimit] = useState<string>('');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCustomLimit('');
  };

  const handleQuickLimitSelect = (limit: number) => {
    handleClose();
    onTestRun(limit);
  };

  const handleCustomLimitApply = () => {
    const value = parseInt(customLimit, 10);
    if (value && value > 0) {
      handleClose();
      onTestRun(value);
    }
  };

  const popoverOpen = Boolean(anchorEl);

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outlined"
        startIcon={<FlaskConical size={16} />}
        disabled={disabled}
        sx={{
          borderWidth: 1,
          py: 1.5,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          minWidth: 120
        }}
      >
        <Typography variant="body2">
          {currentLimit ? `Test: ${currentLimit}` : 'Test Run'}
        </Typography>
      </Button>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List sx={{ width: 250 }}>
          <ListItem disablePadding>
            <Box sx={{ px: 2, py: 1, width: '100%' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Quick Test Limits
              </Typography>
            </Box>
          </ListItem>
          {quickLimits.map((limit) => (
            <ListItem key={limit} disablePadding>
              <ListItemButton
                selected={currentLimit === limit}
                onClick={() => handleQuickLimitSelect(limit)}
              >
                <ListItemText 
                  primary={`${limit} keywords`}
                  primaryTypographyProps={{ fontWeight: currentLimit === limit ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          
          {/* Custom input section */}
          <ListItem sx={{ px: 2, py: 1.5, flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ alignSelf: 'flex-start' }}>
              Custom Limit
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <TextField
                type="number"
                value={customLimit}
                onChange={(e) => setCustomLimit(e.target.value)}
                placeholder="Enter limit"
                size="small"
                fullWidth
                inputProps={{ min: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCustomLimitApply();
                  }
                }}
              />
              <Button
                onClick={handleCustomLimitApply}
                disabled={!customLimit || parseInt(customLimit, 10) <= 0}
                variant="contained"
                size="small"
                sx={{ minWidth: 60, py: 0.5 }}
              >
                Apply
              </Button>
            </Box>
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

export default TestRunButton;

